import { useMemo } from 'react';
import DocumentHeader from '@components/DocumentHeader';
import track, { useTracking } from 'react-tracking';
import TileView, { TileViewContainer } from '@components/TileView';
import If from '@components/If/If';
import { Tile, TileColumn, TileLink, TileSkeleton } from '@components/Tile';
import {
  ROUTE_INVESTING_IDEA_HOME,
  ROUTE_INVESTING_IDEA_DISCOVERY_PAGE_HOME,
  ROUTE_COMMUNITY_IDEA,
} from '@/constants/routes';
import { getGridViewCoverFromImageUrl } from '@/utilities/coverImages';
import {
  getCloudflareImageUrl,
  getResponsiveImage,
} from '@/hooks/useCloudflareImage';
import { useInvestingIdeas } from '../../hooks/useInvestingIdeas';
import { flatten } from 'lodash';
import { Badge } from '@simplywallst/ui-core';

type TypeSlug =
  | 'discover-page-v1'
  | 'discover-page-v2'
  | 'narrative-collection';

const getRouteType = (typeSlug: TypeSlug) => {
  if (typeSlug === 'discover-page-v2')
    return ROUTE_INVESTING_IDEA_DISCOVERY_PAGE_HOME;
  if (typeSlug === 'narrative-collection') return ROUTE_COMMUNITY_IDEA;
  return ROUTE_INVESTING_IDEA_HOME;
};

const ExploreIdeas = () => {
  const { trackEvent } = useTracking();
  const { data, fetchNextPage, hasNextPage, isFetching } = useInvestingIdeas();

  const { keys, entities } = useMemo(
    () => ({
      keys: flatten(data?.pages.map((page) => page.keys) || []),
      entities:
        data?.pages.reduce((a, b) => ({ ...a, ...b.entities }), {}) || {},
    }),
    [data]
  );

  return (
    <>
      <DocumentHeader title="Discover - Investing Ideas" allowCrawlers={true} />
      <If match={keys.length !== 0}>
        <TileViewContainer>
          <TileView
            keys={keys}
            onLoadMore={() => {
              if (hasNextPage) {
                fetchNextPage();
              }
            }}
          >
            {(key, index) => {
              const { name, description, slug, isNew, imageUrl, typeSlug } =
                entities[key];
              const imageSrc = getCloudflareImageUrl({
                src: getGridViewCoverFromImageUrl(imageUrl),
                width: 276,
                height: 188,
                fit: 'crop',
              });

              const responsive = getResponsiveImage({
                src: getGridViewCoverFromImageUrl(imageUrl),
                breakpoints: [
                  { width: 276, height: 188, fit: 'crop' },
                  { width: 163, height: 90, fit: 'crop' },
                ],
              });
              return (
                <TileColumn
                  data-cy-id="idea-tile"
                  col={6}
                  colSm={4}
                  colLg={3}
                  key={key}
                >
                  <TileLink
                    noUnderline
                    inheritColor
                    to={{
                      type: getRouteType(typeSlug),
                      payload: {
                        id: key,
                        name: slug,
                      },
                    }}
                    onClick={() => {
                      window.scrollTo({
                        top: 0,
                        left: 0,
                      });
                      trackEvent({
                        action: 'click',
                        subject: 'tile',
                        gridId: key,
                        index: index,
                      });
                    }}
                  >
                    <Tile
                      coverImage={imageSrc}
                      coverImageSet={responsive.srcSet}
                      name={
                        isNew ? (
                          <>
                            {`${name} `}
                            <Badge tone="attention">New</Badge>
                          </>
                        ) : (
                          name
                        )
                      }
                      description={description}
                    />
                  </TileLink>
                </TileColumn>
              );
            }}
          </TileView>
        </TileViewContainer>
      </If>
      <If match={isFetching}>
        <TileSkeleton />
      </If>
    </>
  );
};

export default track(
  {
    type: 'track',
    subject: 'page',
    action: 'landed',
  },
  { dispatchOnMount: true }
)(ExploreIdeas) as typeof ExploreIdeas;
